/**
 * Compact modal shell (same as RentConfirmationModal: overlay + modalContentCompact + tight header/body/footer + btnPrimary).
 * CircleAlert + bullet list only when `showValidationIcon` is true (validation flows).
 */

import { X, CircleAlert, CircleCheck } from "lucide-react"
import { createPortal } from "react-dom"
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock"
import { NOTIFICATION_TITLES } from "@/constants/notifications"
import modalStyles from "@/features/application/styles/modals.module.css"

function messageLooksLikeHtml(message: string): boolean {
  return /<[a-z][\s\S]*>/i.test(message.trim())
}

interface ValidationModalProps {
  isOpen: boolean
  title?: string
  message: string
  onClose: () => void
  onConfirm?: () => void
  confirmText?: string
  variant?: "error" | "success"
  /**
   * When true (default), show CircleAlert / CircleCheck + bullet list (validation).
   * When false, same copy as {@link RentConfirmationModal} body — plain text or HTML, no icon.
   */
  showValidationIcon?: boolean
  /** Optional E2E hooks (backdrop, close X, primary OK). */
  overlayDataTestId?: string
  closeButtonDataTestId?: string
  confirmButtonDataTestId?: string
  /** Opt into centered, top-aligned validation content for wider messages. */
  alignMessage?: boolean
  /**
   * OK button skin from modals.module.css.
   * `primary` — btnPrimary (panel confirm blue). `saveChanges` — gradient pill (default when alignMessage).
   */
  okButtonStyle?: "primary" | "saveChanges"
}

export function ValidationModal({
  isOpen,
  title,
  message,
  onClose,
  onConfirm,
  confirmText,
  variant = "error",
  showValidationIcon = true,
  overlayDataTestId,
  closeButtonDataTestId,
  confirmButtonDataTestId,
  alignMessage = false,
  okButtonStyle,
}: ValidationModalProps) {
  useBodyScrollLock(isOpen)

  if (!isOpen) return null

  const handleConfirm = () => {
    if (onConfirm) onConfirm()
    onClose()
  }

  const modalTitle = title || (variant === "success" ? NOTIFICATION_TITLES.success : NOTIFICATION_TITLES.missingFields)
  const buttonText = confirmText || "Ok"
  const Icon = variant === "success" ? CircleCheck : CircleAlert
  const iconColor = variant === "success" ? "text-green-600" : "text-red-600"

  const lines = message
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
  const listItems =
    lines.length > 0 ? lines : message.trim() ? [message.trim()] : []
  const validationItems =
    listItems.length > 0 ? listItems : ["Please correct the highlighted fields."]

  const confirmationBody =
    messageLooksLikeHtml(message) ? (
      <div
        className={`${modalStyles.rentConfirmMessage} validation-message-html`}
        dangerouslySetInnerHTML={{ __html: message }}
      />
    ) : (
      <p className={`${modalStyles.rentConfirmMessage} whitespace-pre-line`}>{message}</p>
    )

  const isSingleHtmlMessage =
    validationItems.length === 1 && messageLooksLikeHtml(validationItems[0])

  const validationBody = isSingleHtmlMessage ? (
    <div className={modalStyles.validationMessageIconRow}>
      <div className={modalStyles.validationMessageIcon} aria-hidden>
        <Icon className={`h-4 w-4 ${iconColor}`} strokeWidth={2} />
      </div>
      <div
        className={`${modalStyles.validationMessageHtmlContent} validation-message-html`}
        dangerouslySetInnerHTML={{ __html: validationItems[0] }}
      />
    </div>
  ) : (
    <div className={modalStyles.validationMessageList}>
      {validationItems.map((item, index) => (
        <div key={`${item}-${index}`} className={modalStyles.validationMessageIconRow}>
          <div className={modalStyles.validationMessageIcon} aria-hidden>
            <Icon className={`h-4 w-4 ${iconColor}`} strokeWidth={2} />
          </div>
          {messageLooksLikeHtml(item) ? (
            <div
              className={`${modalStyles.validationMessageHtmlContent} validation-message-html`}
              dangerouslySetInnerHTML={{ __html: item }}
            />
          ) : (
            <p className={`${modalStyles.validationMessageHtmlContent} m-0`}>{item}</p>
          )}
        </div>
      ))}
    </div>
  )

  const resolvedOkButtonStyle =
    okButtonStyle ?? (alignMessage ? "saveChanges" : "primary")
  const okButtonClass =
    resolvedOkButtonStyle === "saveChanges"
      ? modalStyles.btnSaveChangesPrimary
      : modalStyles.btnPrimary

  const content = (
    <div
      className={`${modalStyles.modalOverlay} !z-[2147483640]`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid={overlayDataTestId}
    >
      <div
        className={[
          modalStyles.modalContent,
          modalStyles.modalContentCompact,
          alignMessage ? modalStyles.modalContentValidationAligned : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={modalStyles.validationCompactHeader}>
          <h3 id="validation-modal-title">{modalTitle}</h3>
          <button
            type="button"
            onClick={onClose}
            className={`${modalStyles.closeButton} ${modalStyles.validationCompactClose}`}
            aria-label="Close"
            data-testid={closeButtonDataTestId}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div
          className={[
            modalStyles.validationCompactBody,
            alignMessage ? modalStyles.validationCompactBodyAligned : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {showValidationIcon ? validationBody : confirmationBody}
        </div>
        <div
          className={[
            modalStyles.modalFooterConfirmTight,
            alignMessage ? modalStyles.modalValidationFooterAligned : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <button
            type="button"
            onClick={handleConfirm}
            className={okButtonClass}
            data-testid={confirmButtonDataTestId}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(content, document.body)
}
